// ABA ROTEIRIZAÇÃO — hoje é uma tela de CONFIGURAÇÃO, não de geração.
//
// Até 04/09/2026 esta tela gerava a rota de UM dia (mapa Leaflet, pinos por status, filtros de carteira,
// modo Padrão/Pulverização, "Gerar rota" e "Salvar rota"). Esse motor foi aposentado no app em 29/08 e
// substituído pelo Roteiro da Semana; manter o gerador aqui deixava o gestor produzir — e ENVIAR ao
// vendedor — rota por um motor declarado morto. O gerador saiu; a aba ficou, com os parâmetros.
//
// ⚠️ O QUE ESTES CAMPOS GOVERNAM: o **Roteiro da Semana DO APLICATIVO** (`mobile/utils/smartWeekRoute`).
// Não existe roteirização no painel — a rota é montada no aparelho do vendedor, lendo este mesmo doc
// (`settings/roteirizacao`) toda vez que ele gera o roteiro. Por isso a tela não tem mapa nem prévia:
// não há o que pré-visualizar aqui.
//
// TRÊS PARÂMETROS SAÍRAM DA TELA junto com o motor de 1 dia — `okinalarRedeCodigo`, `multiplicadorRede`
// e `cidadesPulverizacao` eram usados SÓ por ele (o modo "Pulverização Casa" não existe no semanal).
// Continuam no tipo e no doc, sem input: apagar do Firestore não traria benefício e destruiria dado.
import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { RoteirizacaoSettings, DEFAULT_ROTEIRIZACAO } from '../../types';

export default function SmartRoutePage() {
  const { isAdmin: isAdminFull } = useAuth(); // acesso total = quem pode gravar `settings` (firestore.rules)
  const [settings, setSettings] = useState<RoteirizacaoSettings>(DEFAULT_ROTEIRIZACAO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // TEXTO CRU do "Fator de desvio" durante a edição. Um `<input type="number">` controlado devolve `''`
  // enquanto o texto não é um número completo — e "1," / "1." são estados OBRIGATÓRIOS de passagem para
  // digitar 1,4. Com `Number('') === 0` o valor virava 0 e o React reescrevia o campo: era impossível
  // digitar o decimal. Guardando o texto e convertendo só no blur, digita-se normal — e aceita vírgula.
  const [fatorTxt, setFatorTxt] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'roteirizacao'));
        if (cancelado) return;
        // ⚠️ Spread com o DEFAULT, sempre: o doc em produção é mais VELHO que o tipo (não tem
        // fatorDesvio/diasRota/minVisitasDia). Sem isto os campos novos viriam `undefined`, o input
        // controlado viraria não-controlado e o save gravaria lixo por cima do que o motor lê.
        if (snap.exists()) setSettings({ ...DEFAULT_ROTEIRIZACAO, ...snap.data() } as RoteirizacaoSettings);
      } catch (e) {
        console.warn('Roteirização: falha ao carregar parâmetros', e);
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();
    return () => { cancelado = true; };
  }, []);

  const setP = <K extends keyof RoteirizacaoSettings>(k: K, v: RoteirizacaoSettings[K]) =>
    setSettings((prev) => ({ ...prev, [k]: v }));

  const salvar = async () => {
    setSaving(true);
    setMsg('');
    try {
      await setDoc(doc(db, 'settings', 'roteirizacao'), settings, { merge: true });
      // O app relê o doc a cada abertura do Roteiro da Semana; não há push. Quem já estiver com a tela
      // aberta só pega o valor novo na próxima geração.
      setMsg('Parâmetros salvos. Valem na próxima vez que o vendedor gerar o roteiro.');
    } catch {
      setMsg('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Carregando...</p>;

  const numero = (k: keyof RoteirizacaoSettings, extra?: { min?: number; max?: number }) => (
    <input
      type="number"
      min={extra?.min}
      max={extra?.max}
      style={styles.input}
      value={settings[k] as number}
      onChange={(e) => setP(k, Number(e.target.value) as any)}
    />
  );
  const texto = (k: keyof RoteirizacaoSettings, placeholder: string) => (
    <input style={styles.input} value={settings[k] as string} onChange={(e) => setP(k, e.target.value as any)} placeholder={placeholder} />
  );

  return (
    <div>
      <div style={styles.head}>
        <div>
          <h1 style={styles.title}>Roteirização</h1>
          <p style={styles.subtitle}>
            Regras que o <strong>aplicativo</strong> usa para montar o Roteiro da Semana do vendedor.
            A rota é gerada no aparelho — aqui você só define as regras.
          </p>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Jornada</h3>
        <div style={styles.grid}>
          <Field label="Jornada início" hint="HH:MM">{texto('jornadaInicio', '07:30')}</Field>
          <Field label="Jornada fim" hint="HH:MM">{texto('jornadaFim', '17:30')}</Field>
          <Field label="Almoço (min)" hint="Reservado dentro da janela abaixo">{numero('almocoMin', { min: 0 })}</Field>
          <Field label="Almoço — janela início" hint="HH:MM">{texto('almocoJanelaInicio', '12:00')}</Field>
          <Field label="Almoço — janela fim" hint="HH:MM">{texto('almocoJanelaFim', '14:00')}</Field>
          <Field label="Duração da visita (min)" hint="Tempo médio dentro do cliente">{numero('duracaoVisitaMin', { min: 1 })}</Field>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Deslocamento e tamanho da semana</h3>
        <div style={styles.grid}>
          <Field label="Velocidade média (km/h)" hint="Base da estimativa de tempo entre clientes">{numero('velocidadeMediaKmh', { min: 1 })}</Field>
          <Field label="Fator de desvio" hint="Distância de rua ÷ linha reta. 1 = sem correção. Aceita vírgula.">
            <input
              style={styles.input}
              inputMode="decimal"
              value={fatorTxt ?? String(settings.fatorDesvio).replace('.', ',')}
              onChange={(e) => setFatorTxt(e.target.value)}
              onBlur={() => {
                const n = Number(String(fatorTxt ?? '').replace(',', '.'));
                // Fora da faixa volta ao valor ANTERIOR, não ao padrão: o gestor está olhando o campo,
                // e devolver o que ele tinha é o feedback honesto de "não aceitei".
                setP('fatorDesvio', Number.isFinite(n) && n >= 1 && n <= 3 ? n : settings.fatorDesvio);
                setFatorTxt(null);
              }}
              placeholder="1,4"
            />
          </Field>
          <Field label="Dias de rota / zonas" hint="Dias planejados na semana (1 a 7)">{numero('diasRota', { min: 1, max: 7 })}</Field>
          <Field label="Mín. visitas/dia" hint="Piso: o motor completa o dia até chegar aqui">{numero('minVisitasDia', { min: 0, max: 30 })}</Field>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Cadência de visita</h3>
        <div style={styles.grid}>
          <Field label="Ciclo 3★ (dias)" hint="A cada quantos dias revisitar">{numero('ciclo3Dias', { min: 1 })}</Field>
          <Field label="Ciclo 2★ (dias)" hint="A cada quantos dias revisitar">{numero('ciclo2Dias', { min: 1 })}</Field>
          <Field label="Ciclo 1★ (dias)" hint="A cada quantos dias revisitar">{numero('ciclo1Dias', { min: 1 })}</Field>
          <Field label="Janela de tolerância (± dias)" hint="Antecipar/adiar para aproveitar a geografia">{numero('janelaToleranciaDias', { min: 0 })}</Field>
          {/* Fica aqui por vizinhança, mas NÃO é do roteiro: quem lê é o check-out, na web
              (CheckinModal) e no app (ficha do cliente). Sem a dica, parece regra de rota. */}
          <Field label="Cooldown contato frio (dias)" hint="Usado no check-out, não no roteiro">{numero('cooldownContatoFrioDias', { min: 0 })}</Field>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.cardTitle}>Rodízio de São Paulo</h3>
        <label style={styles.check}>
          <input
            type="checkbox"
            checked={!!settings.rodizioSpAtivo}
            onChange={(e) => setP('rodizioSpAtivo', e.target.checked)}
          />
          <span>Aplicar rodízio de São Paulo ao montar o roteiro</span>
        </label>
        <p style={styles.aviso}>
          Com isto ligado, o app evita levar o vendedor ao <strong>Centro Expandido</strong> no dia em
          que a placa dele está restrita, remanejando aqueles clientes para outro dia da semana.
          Só age em quem <strong>preencheu a placa</strong> no Meu Perfil do aplicativo e tem cliente
          dentro da zona — para os demais, nada muda.
        </p>
        <p style={styles.aviso}>
          A placa é de cada vendedor, e é ele quem digita: Meu Perfil → Veículo. O painel não define
          placa por ninguém — carro é pessoal, e uma placa errada tiraria visitas da agenda do colega.
        </p>
      </div>

      {/* `settings` só é gravável por acesso total (firestore.rules) — sem este gate o botão aparecia
          para vendedor/gestor e falhava com permission-denied. */}
      {isAdminFull && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button style={styles.saveBtn} onClick={salvar} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar parâmetros'}
          </button>
          {msg && <span style={{ fontSize: 13, color: msg.startsWith('Erro') ? '#c62828' : '#0a7d33' }}>{msg}</span>}
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      {children}
      {hint && <span style={styles.hint}>{hint}</span>}
    </label>
  );
}

const styles: Record<string, React.CSSProperties> = {
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 10, flexWrap: 'wrap' },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14, maxWidth: 620 },
  card: { background: '#fff', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 12px', fontSize: 16, color: '#1a1a2e' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: '#555' },
  hint: { fontSize: 11, color: '#888' },
  input: { border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1a1a2e', cursor: 'pointer' },
  aviso: { margin: '10px 0 0', fontSize: 12.5, color: '#666', lineHeight: 1.5, maxWidth: 680 },
  saveBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' },
};
